// Engine sound (PC mode only — mobile is silent).
//
// Pre-render a procedural jet-engine loop into an AudioBuffer via
// OfflineAudioContext, then play it through an AudioBufferSourceNode with
// loop=true. AudioBufferSourceNode loops are sample-accurate (no audible gap)
// unlike HTMLAudioElement.loop which has a small silent seam every cycle.
// Speed maps to playbackRate (pitch) and master gain (volume).

const SPEED_MIN = 40;
const SPEED_MAX = 280;
const MASTER_VOLUME = 0.18;
const LOOP_DURATION = 4.0; // seconds — chosen so all oscillator periods loop cleanly
const RAMP = 0.15;

async function renderEngineLoop() {
  const sampleRate = 44100;
  const OfflineCtx =
    window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!OfflineCtx) throw new Error('OfflineAudioContext unavailable');

  const ctx = new OfflineCtx(1, Math.floor(sampleRate * LOOP_DURATION), sampleRate);

  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.85;
  masterGain.connect(ctx.destination);

  // Low rumble: detuned saws → lowpass
  const low1 = ctx.createOscillator();
  low1.type = 'sawtooth'; low1.frequency.value = 80;
  const low2 = ctx.createOscillator();
  low2.type = 'sawtooth'; low2.frequency.value = 80; low2.detune.value = 14;
  const lowMix = ctx.createGain(); lowMix.gain.value = 0.55;
  const lowFilter = ctx.createBiquadFilter();
  lowFilter.type = 'lowpass'; lowFilter.frequency.value = 380; lowFilter.Q.value = 0.7;
  low1.connect(lowMix); low2.connect(lowMix);
  lowMix.connect(lowFilter); lowFilter.connect(masterGain);

  // Mid whine: saw → bandpass
  const mid = ctx.createOscillator();
  mid.type = 'sawtooth'; mid.frequency.value = 350;
  const midFilter = ctx.createBiquadFilter();
  midFilter.type = 'bandpass'; midFilter.frequency.value = 900; midFilter.Q.value = 2.5;
  const midGain = ctx.createGain(); midGain.gain.value = 0.28;
  mid.connect(midFilter); midFilter.connect(midGain); midGain.connect(masterGain);

  // Note: no random-noise component — it would introduce a click at the loop
  // boundary (since random samples don't loop). The lowpassed saws give us a
  // convincing engine rumble on their own.

  low1.start(0); low2.start(0); mid.start(0);

  return await ctx.startRendering(); // returns AudioBuffer
}

export function createEngineAudio() {
  let ctx = null;
  let masterGain = null;
  let source = null;
  let buffer = null;
  let bufferPromise = null;
  let muted = false;
  let started = false;
  let lastVolume = MASTER_VOLUME;

  function preload() {
    if (bufferPromise) return bufferPromise;
    bufferPromise = renderEngineLoop()
      .then((buf) => {
        buffer = buf;
        return buf;
      })
      .catch((e) => {
        console.error('[audio] preload failed:', e);
      });
    return bufferPromise;
  }

  // Must be called inside a user gesture (button click etc).
  function start() {
    if (started) {
      // Already started — make sure context isn't suspended.
      if (ctx?.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      return;
    }
    if (!buffer) {
      // Render not ready yet; once it is, start.
      preload().then(() => start());
      return;
    }

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    if (!ctx) {
      ctx = new AudioCtx();
      masterGain = ctx.createGain();
      masterGain.gain.value = 0;
      masterGain.connect(ctx.destination);
    }
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(masterGain);
    source.start(0);

    started = true;

    const now = ctx.currentTime;
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.setValueAtTime(0, now);
    masterGain.gain.linearRampToValueAtTime(MASTER_VOLUME, now + 0.6);
    lastVolume = MASTER_VOLUME;
  }

  function setSpeed(speedMs) {
    if (!ctx || !started || !source) return;
    const t = Math.max(0, Math.min(1, (speedMs - SPEED_MIN) / (SPEED_MAX - SPEED_MIN)));
    const now = ctx.currentTime;

    source.playbackRate.cancelScheduledValues(now);
    source.playbackRate.linearRampToValueAtTime(0.75 + 0.5 * t, now + RAMP);

    lastVolume = MASTER_VOLUME * (0.55 + 0.45 * t);
    if (!muted) {
      masterGain.gain.cancelScheduledValues(now);
      masterGain.gain.linearRampToValueAtTime(lastVolume, now + RAMP);
    }
  }

  function setMuted(m) {
    muted = m;
    if (!ctx || !masterGain) return;
    const now = ctx.currentTime;
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.linearRampToValueAtTime(m ? 0 : lastVolume, now + RAMP);
  }

  function isMuted() { return muted; }

  function getState() {
    if (!ctx) return 'closed';
    return started ? ctx.state : 'closed';
  }

  function isStarted() { return started; }

  function dispose() {
    if (source) {
      try { source.stop(); } catch {}
      source = null;
    }
    if (ctx) {
      try { ctx.close(); } catch {}
      ctx = null;
    }
    masterGain = null;
    buffer = null;
    bufferPromise = null;
    started = false;
  }

  // Render buffer in the background so it's ready by the time the user
  // taps to enable audio.
  preload();

  return { start, setSpeed, setMuted, isMuted, getState, isStarted, dispose };
}
